import { useMemo, useRef, useState } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { HideContainer } from '@components/hide-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropRight } from '@components/icons/dropright';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconIntegration } from '@components/icons/integration';
import { IconList } from '@components/icons/list';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconMarketplace } from '@components/icons/marketplace';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRestart } from '@components/icons/restart';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconTextShort } from '@components/icons/text-short';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { LabelTextGroup } from '@components/label-text-group';
import { SizedRelativeSpinner, Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import {
  hideSidebar,
  hideExtendedSidebar,
  showExtendedSidebar,
  Sidebar,
  useSidebar,
} from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { TabButton } from '@components/tab-button';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import { EditSyncListTransformPanel } from './edit-sync-list-transform-sidebar';
import { useCreateSyncList, usePatchSyncList, useListSyncTestRequest } from './list-hooks';
import {
  buildArrayLocationPayload,
  buildEndpointUrl,
  buildMappingLabelDefaultValue,
  buildPaginatedUrl,
  createLabelFromKey,
  dotToBracketNotation,
  extractUrlPlaceholders,
  extractValueFromPath,
  filterSingleValueMappingKeys,
  flattenObjectToPaths,
  formatArrayLocationLabel,
  getMappingKeysForArrayLocation,
  getPathFromEndpointUrl,
  hasMultipleArrayLocations,
  isNestedArrayLocation,
  substituteParametersIntoUrl,
} from './list-utils';

import {
  Conditions,
  useConditionState,
  processConditionsForSubmission,
  ConditionType,
} from '../condition';
import {
  useIntegrationList,
  useMarketplaceIntegrationList,
  useLists,
  useListsScope,
  ListsScopeProvider,
} from '../integrations-hooks';
import type {
  List,
  ListSyncTestRequestType,
  SyncListCreateData,
  SyncListConditions,
  SyncListParameter,
  SortOrder,
} from '../integrations-types';

type SyncListFormType = {
  name: string;
  description?: string;
  integration?: string;
  url?: string;
  path?: string;
  mappingLabel: VariableValue;
  mappingValue: string;
  conditionType?: ConditionType;
  sortOrder?: SortOrder;
  paginationTokenPath?: string;
  paginationParamName?: string;
  transformJq?: string | null;
  graphqlQuery: string;
  // Per-URL-placeholder fields are registered dynamically — see parameterLabelFieldId / parameterTestValueFieldId.
  [parameterField: `parameter-${string}-label`]: string;
  [parameterField: `parameter-${string}-test-value`]: string;
};

const parameterLabelFieldId = (name: string) => `parameter-${name}-label` as const;
const parameterTestValueFieldId = (name: string) => `parameter-${name}-test-value` as const;

const Form = createForm<SyncListFormType>();

export const IntegrationThumbnail = ({ thumbnail }: { thumbnail?: string | null }) => {
  if (thumbnail) {
    return <SquareImage src={thumbnail} />;
  }
  return <IconIntegration color="elementPurpleInvert" />;
};

export type SyncListSidebarProps = {
  list?: List;
  baseUrl?: string; // For Marketplace: use path-based URL building
  integrationId?: string; // For Marketplace: pre-selected integration ID
  templateId?: string; // For Marketplace: template context for API calls
  connectionVariableNames?: string[]; // URL placeholders matching these are filled at connect time, not by the list.
};

export const SyncListSidebar = ({
  list,
  baseUrl,
  integrationId,
  templateId,
  connectionVariableNames,
}: SyncListSidebarProps) => {
  return (
    <ListsScopeProvider templateId={templateId}>
      <SyncListSidebarContent
        list={list}
        baseUrl={baseUrl}
        integrationId={integrationId}
        connectionVariableNames={connectionVariableNames}
      />
    </ListsScopeProvider>
  );
};

const SyncListSidebarContent = ({
  list,
  baseUrl,
  integrationId: defaultIntegrationId,
  connectionVariableNames,
}: Omit<SyncListSidebarProps, 'templateId'>) => {
  const { templateId: scopedTemplateId } = useListsScope();
  const allowDynamicList = Boolean(scopedTemplateId);
  const showIntegrationSelect = !defaultIntegrationId;
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore({
    values: { transformJq: list?.sync?.transformJq ?? null },
  });
  const isDirty = formStore.useIsDirty();
  const importingModal = useSyncListImportingModal();

  const listsQuery = useLists();
  const integrationsQuery = useIntegrationList();
  const marketplaceQuery = useMarketplaceIntegrationList();
  const createMutation = useCreateSyncList();
  const editMutation = usePatchSyncList();

  const [sourceType, setSourceType] = useState<'custom' | 'marketplace'>(
    list?.sync?.integration?.isFromMarketplace ? 'marketplace' : 'custom'
  );
  const [isGraphql, setIsGraphql] = useState(Boolean(list?.sync?.graphqlRequest));

  const listName = formStore.useFieldValue('name') as string;
  const integration = formStore.useFieldValue('integration') as string;
  const url = formStore.useFieldValue('url') as string;
  const path = formStore.useFieldValue('path') as string;
  const paginationTokenPath = formStore.useFieldValue('paginationTokenPath') as string;
  const paginationParamName = formStore.useFieldValue('paginationParamName') as string;
  const editedGraphqlQuery = formStore.useFieldValue('graphqlQuery') as string | undefined;
  // The editor only reports edits, so an untouched field means the stored query.
  const graphqlQuery = editedGraphqlQuery ?? list?.sync?.graphqlRequest ?? '';

  const currentIntegrationId = integration || defaultIntegrationId || '';
  const testMutation = useListSyncTestRequest(currentIntegrationId);

  const [responseStatus, setResponseStatus] = useState<'initial' | 'success' | 'error'>(
    list ? 'success' : 'initial'
  );
  const [testResponse, setTestResponse] = useState<ListSyncTestRequestType | null>(null);
  const [selectedArrayLocation, setSelectedArrayLocation] = useState<string | undefined>(
    list?.sync?.arrayLocation
  );
  const isEdit = Boolean(list);

  const transformJq = formStore.useFieldValue('transformJq') as string | null;

  const showInitialMappingRef = useRef(false);
  const testRequestModal = useSyncListTestRequestModal();
  const formValues = formStore.useFormValues();

  const initialConditions = useMemo(
    () => list?.sync?.conditions?.conditions ?? [],
    [list?.sync?.conditions?.conditions]
  );
  const {
    conditions,
    setConditions,
    conditionsChanged,
    handleAddCondition,
    handleRemoveCondition,
  } = useConditionState(initialConditions);

  const getConditionsForSubmission = (): SyncListConditions | null => {
    if (conditions.length === 0) {
      return null;
    }
    const processedConditions = processConditionsForSubmission(conditions, formValues);
    const conditionType = (formValues.conditionType as 'all' | 'any') ?? 'all';
    return {
      conditions: processedConditions,
      conditionType,
    };
  };

  const integrationOptions = useMemo(() => {
    if (!integrationsQuery.data) return [];
    return integrationsQuery.data
      .filter((int) => int.authentication?.isAuthenticated)
      .map((int) => ({
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
              <IntegrationThumbnail thumbnail={int.thumbnail} />
            </div>
            {int.name}
          </div>
        ),
        value: int.shortId,
        filterText: int.name,
      }));
  }, [integrationsQuery.data]);

  const marketplaceOptions = useMemo(() => {
    if (!marketplaceQuery.data) return [];
    return marketplaceQuery.data.map((marketplaceIntegration) => ({
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
            <IntegrationThumbnail thumbnail={marketplaceIntegration.logo} />
          </div>
          {marketplaceIntegration.name}
        </div>
      ),
      value: marketplaceIntegration.shortId,
      filterText: marketplaceIntegration.name,
    }));
  }, [marketplaceQuery.data]);

  const sourceOptions = sourceType === 'marketplace' ? marketplaceOptions : integrationOptions;

  const listSourceType: 'custom' | 'marketplace' = list?.sync?.integration?.isFromMarketplace
    ? 'marketplace'
    : 'custom';
  // Only pre-fill the edited list's integration on its own tab; the other tab starts empty.
  const sourceDefaultValue =
    sourceType === listSourceType ? list?.sync?.integration?.shortId : undefined;

  const handleSourceTypeChange = (nextSourceType: 'custom' | 'marketplace') => {
    setSourceType(nextSourceType);
    setTestResponse(null);
    setResponseStatus('initial');
    showInitialMappingRef.current = true;
    // Switching source resets the integration, so the old jq no longer applies.
    formStore.actions.setValue('transformJq', null);
  };

  const existingListNames = useMemo(() => {
    return listsQuery.data?.map((l) => l.name) ?? [];
  }, [listsQuery.data]);

  const testUrl = useMemo(() => {
    if (baseUrl && path !== undefined) {
      return buildEndpointUrl(baseUrl, path ?? '');
    }
    return url ?? '';
  }, [baseUrl, path, url]);

  const parameterNames = useMemo(() => {
    if (!allowDynamicList) return [];
    const connectionVars = new Set(connectionVariableNames ?? []);
    return extractUrlPlaceholders(testUrl).filter((name) => !connectionVars.has(name));
  }, [testUrl, connectionVariableNames, allowDynamicList]);

  const isDynamicList = parameterNames.length > 0;

  const parameters: SyncListParameter[] = useMemo(
    () =>
      parameterNames.map((name) => {
        const previous = list?.sync?.parameters?.find((parameter) => parameter.name === name);
        return {
          name,
          label: (formValues[parameterLabelFieldId(name)] as string) ?? previous?.label ?? name,
          testValue:
            (formValues[parameterTestValueFieldId(name)] as string) ?? previous?.testValue ?? '',
        };
      }),
    [parameterNames, formValues, list?.sync?.parameters]
  );

  const allParameterTestValuesFilled = parameters.every(
    (parameter) => parameter.testValue.trim() !== ''
  );

  // For dynamic lists the URL has placeholders that the parameter test values fill.
  const resolvedTestUrl = useMemo(
    () =>
      isDynamicList
        ? substituteParametersIntoUrl(
            testUrl,
            Object.fromEntries(parameters.map(({ name, testValue }) => [name, testValue]))
          )
        : testUrl,
    [isDynamicList, testUrl, parameters]
  );

  const paginationPathOptions = useMemo(() => {
    if (!testResponse?.exampleData) return [];
    return flattenObjectToPaths(testResponse.exampleData)
      .filter((p) => p.type !== 'object' && p.type !== 'array')
      .map((p) => ({ value: p.path, label: dotToBracketNotation(p.path) }));
  }, [testResponse?.exampleData]);

  const paginationPreviewUrl = useMemo(() => {
    if (!paginationTokenPath || !paginationParamName || !testResponse?.exampleData || !testUrl)
      return undefined;
    const value = extractValueFromPath(testResponse.exampleData, paginationTokenPath);
    if (value === undefined) return undefined;
    return buildPaginatedUrl(testUrl, paginationParamName, String(value));
  }, [paginationTokenPath, paginationParamName, testResponse?.exampleData, testUrl]);

  // The endpoint returns failures as HTTP 200, so status comes from success, not a catch.
  const applyTestResponse = (response: ListSyncTestRequestType) => {
    setTestResponse(response);
    if (response.arrayLocations && response.arrayLocations.length > 0) {
      const previousPickStillValid = response.arrayLocations.some(
        (option) => option.name === selectedArrayLocation
      );
      if (!previousPickStillValid) {
        setSelectedArrayLocation(undefined);
      }
    } else {
      setSelectedArrayLocation(undefined);
    }
    setResponseStatus(response.success ? 'success' : 'error');
  };

  const handleTestEndpoint = async () => {
    if (baseUrl) {
      changePath(path);
    } else {
      changeSource(integration, url);
    }
    try {
      const response = await testMutation.mutateAsync({
        url: resolvedTestUrl,
        transformJq: transformJq ?? undefined,
        graphqlRequest: isGraphql ? graphqlQuery : undefined,
      });
      applyTestResponse(response);
      testRequestModal({ response });
    } catch {
      setResponseStatus('error');
    }
  };

  const showArrayLocationPicker = hasMultipleArrayLocations(testResponse);
  // The merge only applies to a nested path, and that can be auto-selected
  // without the picker ever showing.
  const showNestedArrayMergeHelp = isNestedArrayLocation(selectedArrayLocation, testResponse);
  const mappingKeys = useMemo(() => {
    return getMappingKeysForArrayLocation(
      testResponse,
      selectedArrayLocation,
      list?.sync?.mappingKeys
    );
  }, [testResponse, selectedArrayLocation, list?.sync?.mappingKeys]);
  // Label + value options hold a single value: an option is a { label, value }
  // pair, so array/object keys are never valid targets. Conditions keep the full set.
  const options = useMemo(() => {
    return filterSingleValueMappingKeys(mappingKeys).map((key) => ({
      label: key.name,
      value: key.name,
      type: key.type,
    }));
  }, [mappingKeys]);

  const arrayLocationOptions = useMemo(() => {
    return (testResponse?.arrayLocations ?? []).map((option) => ({
      value: option.name,
      label: formatArrayLocationLabel(option.name),
    }));
  }, [testResponse?.arrayLocations]);

  const handleArrayLocationChange = (value: string) => {
    setSelectedArrayLocation(value);
    showInitialMappingRef.current = true;
  };

  const variableGroups: FieldMappingGroup[] = useMemo(
    () => [
      {
        id: 'list',
        label: 'List',
        fields: options.map((option) => ({
          label: option.label,
          value: option.value,
          name: option.value,
          source: option.value,
          field: option.type,
          type: option.type as FieldType,
        })),
        icon: <IconTextShort />,
      },
    ],
    [options]
  );

  // Conditions need 'payload_' prefix for backend resolution. Unlike the
  // label/value pickers, conditions keep the full key set (incl. arrays).
  const conditionVariableGroups: FieldMappingGroup[] = useMemo(
    () => [
      {
        id: 'payload',
        label: 'Payload',
        fields: mappingKeys.map((key) => ({
          label: key.name,
          value: key.name,
          name: key.name,
          source: 'payload',
          field: key.name,
          type: key.type as FieldType,
        })),
        icon: <IconTextShort />,
      },
    ],
    [mappingKeys]
  );

  const defaultMappingLabel = useMemo(() => {
    const fields = variableGroups.length > 0 ? variableGroups[0].fields : [];
    return buildMappingLabelDefaultValue(list?.sync?.mappingLabel ?? '', fields);
  }, [list?.sync?.mappingLabel, variableGroups]);

  const changeSource = (integrationValue: string, urlValue: string, isUserEdit = false) => {
    setTestResponse(null);
    if (
      integrationValue === list?.sync?.integration.shortId &&
      urlValue === list?.sync?.endpointUrl
    ) {
      setResponseStatus('success');
      showInitialMappingRef.current = false;
    } else {
      setResponseStatus('initial');
      showInitialMappingRef.current = true;
      // The jq was written for the previous endpoint's shape — drop it so it
      // isn't re-tested or saved against a different one.
      if (isUserEdit) {
        formStore.actions.setValue('transformJq', null);
      }
    }
  };

  const changePath = (pathValue: string, isUserEdit = false) => {
    setTestResponse(null);
    const defaultPath = getPathFromEndpointUrl(baseUrl ?? '', list?.sync?.endpointUrl);
    if (pathValue === defaultPath) {
      setResponseStatus('success');
      showInitialMappingRef.current = false;
    } else {
      setResponseStatus('initial');
      showInitialMappingRef.current = true;
      if (isUserEdit) {
        formStore.actions.setValue('transformJq', null);
      }
    }
  };

  const canTest =
    (baseUrl ? Boolean(path) || isGraphql : Boolean(integration && url)) &&
    (!isGraphql || Boolean(graphqlQuery.trim())) &&
    (!isDynamicList || allParameterTestValuesFilled);

  const handleSetTransformJq = async (jq: string | null) => {
    formStore.actions.setValue('transformJq', jq);
    // New field shape — mapping pickers re-pick instead of re-seeding the old value.
    showInitialMappingRef.current = true;
    hideExtendedSidebar();
    // Don't re-test with an unfilled placeholder URL.
    if (!canTest) {
      return;
    }
    try {
      const response = await testMutation.mutateAsync({
        url: resolvedTestUrl,
        transformJq: jq ?? undefined,
        graphqlRequest: isGraphql ? graphqlQuery : undefined,
      });
      applyTestResponse(response);
    } catch {
      // Keep the change applied even if the re-test fails; the user can re-test manually.
      setResponseStatus('error');
    }
  };

  // The extended panel captures onSave once when it opens, so call the latest handler
  // through a ref — otherwise saving would re-test the URL from when the panel opened.
  const handleSetTransformJqRef = useRef(handleSetTransformJq);
  handleSetTransformJqRef.current = handleSetTransformJq;

  const openTransformSidebar = () => {
    showExtendedSidebar({
      component: EditSyncListTransformPanel,
      props: {
        exampleData: testResponse?.exampleData ?? null,
        exampleDataWrapped: testResponse?.exampleDataWrapped ?? false,
        initialJq: transformJq ?? '',
        onSave: (jq: string | null) => handleSetTransformJqRef.current(jq),
        onClose: hideExtendedSidebar,
      },
      extendedId: 'sync-list-transform',
    });
  };

  const handleCreateList = async (formData: SyncListFormType) => {
    const data: SyncListCreateData = {
      name: formData.name,
      description: formData.description?.trim(),
      integrationId: formData.integration ?? defaultIntegrationId ?? '',
      endpointUrl: testUrl,
      mappingLabel: formData.mappingLabel.value,
      mappingValue: formData.mappingValue,
      isActive: true,
      conditions: getConditionsForSubmission(),
      sortOrder: formData.sortOrder,
      paginationTokenPath: formData.paginationTokenPath || null,
      paginationParamName: formData.paginationParamName || null,
      parameters: isDynamicList ? parameters : undefined,
      transformJq: transformJq || null,
      graphqlRequest: isGraphql ? graphqlQuery || null : null,
      ...(buildArrayLocationPayload(selectedArrayLocation, testResponse) ?? {}),
    };

    if (isEdit && list?.sync) {
      await editMutation.mutateAsync({ id: list.sync.shortId, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    hideSidebar();
    if (!isDynamicList) {
      importingModal();
    }
  };

  const hasChanges =
    isDirty || conditionsChanged || isGraphql !== Boolean(list?.sync?.graphqlRequest);

  const duplicateListName = existingListNames.includes(listName) && listName !== list?.name;

  const responseStatusMap: Record<
    'initial' | 'success' | 'error',
    { Icon: React.ComponentType<{ color?: Color; size?: number }>; text: string; color: Color }
  > = {
    initial: {
      Icon: IconFormIndicatorInfo,
      text: t('testNotPerformed'),
      color: 'elementIndigo100',
    },
    success: { Icon: IconFormIndicatorSuccess, text: t('testSuccess'), color: 'elementGreen100' },
    error: {
      Icon: IconFormIndicatorError,
      text: testResponse?.error ?? t('testError'),
      color: 'elementRed100',
    },
  };

  const { Icon, text, color } = responseStatusMap[responseStatus];

  const defaultPath = baseUrl
    ? getPathFromEndpointUrl(baseUrl, list?.sync?.endpointUrl)
    : undefined;
  const hasPagination = Boolean(list?.sync?.paginationTokenPath || list?.sync?.paginationParamName);
  // A failed test still returns example_data (so the editor can reshape it), but a
  // genuinely empty payload has nothing to transform.
  const hasTestData = Boolean(
    testResponse?.exampleData && Object.keys(testResponse.exampleData).length > 0
  );

  if (showIntegrationSelect && !integrationsQuery.data) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader
          icon={IconList}
          iconColor="elementBlue100"
          title={isEdit ? t('editSyncList') : t('addSyncList')}
        />
        <Sidebar.Content>
          <Spinner />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconList}
        iconColor="elementBlue100"
        title={isEdit ? t('editSyncList') : t('addSyncList')}
      />
      <Form store={formStore} onSubmit={handleCreateList}>
        <Form.HiddenField id="transformJq" defaultValue={list?.sync?.transformJq ?? null} />
        <Sidebar.Content>
          <Stack space={4}>
            <HideContainer title={t('listDetails')}>
              <Stack space={4}>
                <Stack space={1}>
                  <Form.TextField
                    id="name"
                    placeholder={t('addListName')}
                    label={t('listName')}
                    autoFocus
                    validators={['required']}
                    defaultValue={list?.name}
                  />
                  {duplicateListName && <FormError text={t('duplicateListTitle')} />}
                </Stack>
                <Form.Textarea
                  id="description"
                  label={t('description')}
                  defaultValue={list?.description ?? ''}
                />
                <Form.Select
                  id="sortOrder"
                  label={t('sorting')}
                  options={[
                    { value: 'api_order', label: t('apiOrder') },
                    { value: 'alphabetical', label: t('alphabetical') },
                  ]}
                  defaultValue={list?.sync?.sortOrder ?? 'api_order'}
                  isClearable={false}
                />
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('source')}>
              <Stack space={4}>
                {showIntegrationSelect && (
                  <>
                    <Text text={t('sourceInfo')} font="regular14" />
                    {marketplaceQuery.isEnabled && (
                      <TabButton<'custom' | 'marketplace'>
                        value={sourceType}
                        onChange={handleSourceTypeChange}
                        fullWidth
                        options={[
                          {
                            value: 'custom',
                            label: t('sourceTypeCustom'),
                            icon: <IconIntegration />,
                          },
                          {
                            value: 'marketplace',
                            label: t('sourceTypeMarketplace'),
                            icon: <IconMarketplace />,
                          },
                        ]}
                      />
                    )}
                    <Form.Select
                      id="integration"
                      key={sourceType}
                      isClearable={false}
                      label={t('sourceIntegration')}
                      options={sourceOptions}
                      validators={['required']}
                      onChange={(value) => changeSource(value, url, true)}
                      defaultValue={sourceDefaultValue}
                      hasFilterText
                    />
                  </>
                )}
                <Select
                  label={t('requestType')}
                  options={[
                    { value: 'json', label: t('requestTypeJson') },
                    { value: 'graphql', label: t('requestTypeGraphql') },
                  ]}
                  value={isGraphql ? 'graphql' : 'json'}
                  onChange={(value) => setIsGraphql(value === 'graphql')}
                  isClearable={false}
                />
                {baseUrl ? (
                  <Stack space={1}>
                    <Form.TextField
                      id="path"
                      placeholder={`${t('addPath')}...`}
                      label={t('path')}
                      validators={isGraphql ? [] : ['required']}
                      onChange={(value) => changePath(value, true)}
                      defaultValue={defaultPath}
                    />
                    <Text font="regular12" text={`${baseUrl}${path ?? ''}`} color="textHelper" />
                  </Stack>
                ) : (
                  <Form.TextField
                    id="url"
                    placeholder={`${t('addUrl')}...`}
                    label={t('sourceUrl')}
                    validators={['required', 'valid-http-url']}
                    onChange={(value) => changeSource(integration, value, true)}
                    defaultValue={list?.sync?.endpointUrl}
                  />
                )}
                {isGraphql && (
                  <Form.GraphQLEditor
                    id="graphqlQuery"
                    label={t('graphqlQuery')}
                    initialValue={list?.sync?.graphqlRequest ?? ''}
                  />
                )}
              </Stack>
            </HideContainer>
            {isDynamicList && (
              <>
                <Separator />
                <HideContainer title={t('parameters')}>
                  <Stack space={4}>
                    <Text text={t('parametersInfo')} font="regular14" color="textHelper" />
                    {parameterNames.map((name) => {
                      const previous = list?.sync?.parameters?.find((p) => p.name === name);
                      return (
                        <Card key={name} space={4} variant="gray">
                          <Stack space={3}>
                            <LabelTextGroup label={t('parameterName')} text={name} />
                            <Form.TextField
                              id={parameterLabelFieldId(name)}
                              label={t('parameterLabel')}
                              defaultValue={previous?.label ?? createLabelFromKey(name)}
                            />
                            <Form.TextField
                              id={parameterTestValueFieldId(name)}
                              label={t('parameterTestValue')}
                              defaultValue={previous?.testValue ?? ''}
                            />
                          </Stack>
                        </Card>
                      );
                    })}
                  </Stack>
                </HideContainer>
              </>
            )}
            <Separator />
            <HideContainer title={t('testResponse')}>
              <Stack space={4}>
                <Text text={t('testResponseInfo')} font="regular14" />
                <Stack space={4} orientation="horizontal" align="center">
                  <Button
                    iconLeft={responseStatus === 'success' ? <IconRestart /> : <IconSend />}
                    isDisabled={!canTest || testMutation.isLoading}
                    text={responseStatus === 'success' ? t('reTest') : t('test')}
                    variant={responseStatus === 'success' ? 'secondary' : 'primary-blue'}
                    onClick={() => handleTestEndpoint()}
                  />
                  {testMutation.isLoading ? (
                    <SizedRelativeSpinner size={24} />
                  ) : (
                    <Stack space={2} orientation="horizontal" align="center">
                      <Icon color={color} size={16} />
                      <Text text={text} font="regular14" color={color} />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('customOptions')} defaultClosed={!list?.sync?.transformJq}>
              {!transformJq ? (
                <Button
                  text={t('addCustomOptions')}
                  iconLeft={<IconAddPlus />}
                  variant="secondary"
                  onClick={openTransformSidebar}
                  isDisabled={!hasTestData}
                  tooltip={!hasTestData ? t('customOptionsDisabledReason') : undefined}
                />
              ) : (
                <Stack space={2}>
                  <Stack space={2} orientation="horizontal" align="center" justify="space-between">
                    <Text font="semibold14" text={t('core.jqQuery')} />
                    <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
                      <MenuItem
                        icon={<IconEdit />}
                        label={t('core.edit')}
                        isDisabled={!hasTestData}
                        tooltip={!hasTestData ? t('editDisabledReason') : undefined}
                        onSelect={openTransformSidebar}
                      />
                      <MenuItem
                        icon={<IconTrash />}
                        label={t('core.delete')}
                        variant="danger"
                        onSelect={() => handleSetTransformJq(null)}
                      />
                    </Menu>
                  </Stack>
                  <Text font="regular14" text={transformJq} style={{ overflowWrap: 'anywhere' }} />
                </Stack>
              )}
            </HideContainer>
            <Separator />
            <HideContainer title={t('mapFields')}>
              {responseStatus !== 'success' ? (
                <Card space={6} variant="gray">
                  <Stack space={4} orientation="horizontal">
                    <IconMappingOff />
                    <Text text={t('mapFieldsInfo')} font="semibold12" />
                  </Stack>
                </Card>
              ) : (
                <Stack space={4}>
                  {showArrayLocationPicker && (
                    <Stack space={1}>
                      <Select
                        label={t('arrayLocationLabel')}
                        options={arrayLocationOptions}
                        value={selectedArrayLocation}
                        onChange={handleArrayLocationChange}
                        isClearable={false}
                        placeholder={t('arrayLocationPlaceholder')}
                      />
                      <Text text={t('arrayLocationHelp')} font="regular12" color="textHelper" />
                    </Stack>
                  )}
                  {showNestedArrayMergeHelp && (
                    <InfoBox variant="indigo" text={t('nestedArrayMergeHelp')} />
                  )}
                  <Form.FieldMappingTextEditor
                    id="mappingLabel"
                    key={`mapping-label-${responseStatus}-${selectedArrayLocation ?? ''}-${
                      transformJq ?? ''
                    }`}
                    label={t('label')}
                    singleLine
                    plaintext
                    fieldMappingId={`synced-list-${list?.sync?.shortId ?? 'new'}`}
                    variableGroups={variableGroups}
                    data-testid="label-field"
                    validators={['required']}
                    defaultValue={!showInitialMappingRef.current ? defaultMappingLabel : undefined}
                  />
                  <Form.Select
                    id="mappingValue"
                    key={`mapping-value-${responseStatus}-${selectedArrayLocation ?? ''}-${
                      transformJq ?? ''
                    }`}
                    label={t('value')}
                    options={options ?? []}
                    validators={['required']}
                    defaultValue={
                      !showInitialMappingRef.current ? list?.sync?.mappingValue : undefined
                    }
                  />
                </Stack>
              )}
            </HideContainer>
            {responseStatus === 'success' && (
              <>
                <Separator />
                <HideContainer title={t('addConditions')}>
                  <Conditions
                    conditions={conditions}
                    conditionType={list?.sync?.conditions?.conditionType}
                    variableGroups={conditionVariableGroups}
                    fieldMappingId={`synced-list-conditions-${list?.sync?.shortId ?? 'new'}`}
                    onAddCondition={handleAddCondition}
                    onRemoveCondition={handleRemoveCondition}
                    setConditions={setConditions}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Form={Form as any}
                    helpText={t('conditionHelptext')}
                  />
                </HideContainer>
                <Separator />
                <HideContainer title={t('pagination')} defaultClosed={!hasPagination}>
                  <Stack space={4}>
                    <Text
                      text={isGraphql ? t('paginationInfoGraphql') : t('paginationInfo')}
                      font="regular14"
                      color="textHelper"
                    />
                    {paginationPathOptions.length > 0 ? (
                      <Form.Select
                        id="paginationTokenPath"
                        label={t('tokenPath')}
                        options={paginationPathOptions}
                        defaultValue={list?.sync?.paginationTokenPath}
                        placeholder={t('selectTokenPath')}
                      />
                    ) : list?.sync?.paginationTokenPath ? (
                      <LabelTextGroup
                        label={t('tokenPath')}
                        text={`${dotToBracketNotation(list.sync.paginationTokenPath)} (${t(
                          'retestToChange'
                        )})`}
                      />
                    ) : isGraphql && testResponse ? (
                      <Text text={t('pageNumbersSent')} font="regular12" color="textHelper" />
                    ) : (
                      <Text text={t('testToSelectPath')} font="regular12" color="textHelper" />
                    )}
                    <Form.TextField
                      id="paginationParamName"
                      label={isGraphql ? t('variableName') : t('paramName')}
                      defaultValue={list?.sync?.paginationParamName || undefined}
                    />
                    {paginationPreviewUrl && !isGraphql && (
                      <LabelTextGroup label={t('nextUrl')} text={paginationPreviewUrl} />
                    )}
                    <PaginationLearnMore isGraphql={isGraphql} />
                  </Stack>
                </HideContainer>
              </>
            )}
          </Stack>
        </Sidebar.Content>
        <Sidebar.Footer>
          <Form.Actions>
            <ButtonGroup align="right">
              <Button onClick={hideSidebar} text={t('core.cancel')} variant="secondary" />
              <Form.Submit variant="primary-blue" text={t('save')} isDisabled={!hasChanges} />
            </ButtonGroup>
          </Form.Actions>
        </Sidebar.Footer>
      </Form>
    </Sidebar>
  );
};

const useSyncListTestRequestModal = () =>
  useModal(
    (props: { response: ListSyncTestRequestType }) => (
      <SyncListTestRequest response={props.response} />
    ),
    { variant: 'LARGE' }
  );

type TestModalProps = {
  response: ListSyncTestRequestType;
};

const SyncListTestRequest = ({ response }: TestModalProps) => {
  const { t } = useScopedTranslate(locale);
  const { exampleData, statusCode, success } = response;
  return (
    <Modal>
      <Modal.Header>
        <Modal.HeaderTitle title={t('testResponse')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={4}>
          <Stack orientation="horizontal" align="center">
            <IconStatusIndicator color={success ? 'elementGreen200' : 'elementRed100'} />
            <Text
              font="regular14"
              color={success ? 'elementGreen200' : 'elementRed100'}
              text={`${success ? t('success') : t('fail')} • ${statusCode}`}
            />
          </Stack>
          <CodeBlock data={exampleData} hideFooter />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const useSyncListImportingModal = () => useModal(SyncListImporting, { variant: 'LARGE' });

const SyncListImporting = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Modal>
      <Modal.Header>
        <Modal.HeaderTitle title={t('importing')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={1}>
          <Text text={t('syncProcessed')} font="bold16" />
          <Text text={t('syncTime')} />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

export const useSyncListSidebar = () => {
  return useSidebar(SyncListSidebar, {
    variant: 'REGULAR',
    closeOnClickOutside: true,
  });
};

export const PaginationLearnMore = ({ isGraphql = false }: { isGraphql?: boolean }) => {
  const { t } = useScopedTranslate(locale);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Stack space={2}>
      <Stack
        space={1}
        orientation="horizontal"
        align="center"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        {isOpen ? (
          <IconDropdown size={16} color="elementIndigo100" />
        ) : (
          <IconDropRight size={16} color="elementIndigo100" />
        )}
        <Text text={t('learnMore')} font="regular12" color="elementIndigo100" />
      </Stack>
      {isOpen && (
        <Text
          text={isGraphql ? t('paginationExampleGraphql') : t('paginationExample')}
          font="regular12"
          color="textHelper"
        />
      )}
    </Stack>
  );
};

const locale = {
  en: {
    addSyncList: 'Add sync list',
    editSyncList: 'Edit sync list',
    listDetails: 'List details',
    listName: 'List name',
    addListName: 'Add list name',
    description: 'Description',
    source: 'Source',
    sourceInfo:
      'Select the third-party integration that you will use as your primary source for this list',
    sourceIntegration: 'Source integration',
    sourceTypeCustom: 'Custom integrations',
    sourceTypeMarketplace: 'Marketplace integrations',
    sourceUrl: 'Source URL',
    addUrl: 'Add url',
    path: 'Path',
    addPath: 'Add path',
    requestType: 'Request type',
    requestTypeJson: 'JSON',
    requestTypeGraphql: 'GraphQL',
    graphqlQuery: 'GraphQL query',
    testResponse: 'Test response',
    testResponseInfo: 'Checks that the list response is correct.',
    test: 'Test',
    testNotPerformed: 'Test not performed',
    testSuccess: 'Test successful',
    testError: 'Test failed',
    reTest: 'Re-test',
    mapFields: 'Map fields',
    mapFieldsInfo: 'A valid test response has to be received before fields can be mapped',
    label: 'Label',
    value: 'Value',
    save: 'Save',
    duplicateListTitle: 'List with this name already exists',
    success: 'Success',
    fail: 'Fail',
    importing: 'Importing',
    syncProcessed: 'Sync is being processed in the background',
    syncTime:
      'Please note this can take some time depending on how many list options are being added',
    addConditions: 'Add conditions',
    conditionHelptext: 'Only include items that match the following conditions',
    sorting: 'Sorting',
    apiOrder: 'Default (as received)',
    alphabetical: 'Alphabetical',
    pagination: 'Pagination (optional)',
    paginationInfo:
      'Use this if your API returns multiple pages but does not include a "next page" URL.',
    learnMore: 'Learn more',
    paginationExample:
      'Token path: Where to find the next page value in the response (e.g. next_cursor)\n\nQuery parameter: The parameter used to request the next page (e.g. cursor)\n\nExample: If the response includes "next_cursor": "abc123" and the next request uses ?cursor=abc123, then Token path = next_cursor and Query param = cursor',
    paginationInfoGraphql:
      'Use this if your API returns multiple pages. The next page value is sent back as a variable in your query, so the query has to declare it.',
    paginationExampleGraphql:
      'Token path: Where to find the next page value in the response (e.g. page[cursor])\n\nQuery variable: The variable your query uses to ask for the next page (e.g. cursor)\n\nYour query must declare that variable and pass it through, for example:\n\nquery($cursor: String) {\n  items(cursor: $cursor) {\n    cursor\n    nodes { id name }\n  }\n}',
    tokenPath: 'Token path in response',
    paramName: 'Query parameter name',
    variableName: 'Query variable name',
    pageNumbersSent:
      'No next page value in this response, so the page number is sent as the variable instead.',
    selectTokenPath: 'Select token path',
    retestToChange: 're-test to change',
    testToSelectPath: 'Test endpoint to select token path',
    nextUrl: 'Next page URL',
    parameters: 'Parameters',
    parametersInfo:
      'Parts of this URL are filled in each time the list loads. Add a test value for each so you can check the response.',
    parameterName: 'Parameter',
    parameterLabel: 'Label',
    parameterTestValue: 'Test value',
    arrayLocationLabel: 'Array',
    arrayLocationPlaceholder: 'Pick an array',
    arrayLocationHelp: 'The response has more than one array. Pick the one to sync.',
    nestedArrayMergeHelp:
      'This array is nested, so an item appearing under more than one parent is merged into a single option.',
    customOptions: 'Custom options',
    addCustomOptions: 'Add custom options',
    customOptionsDisabledReason: 'Test the endpoint first before adding custom options.',
    editDisabledReason: 'Re-test the endpoint to edit the custom options.',
  },
};
